import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Shield, AlertTriangle, Loader2, Clock, CheckCircle2, XOctagon,
  RefreshCw, ShieldCheck, Users, CalendarClock, ArrowUpCircle, Bell,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminComplianceDashboard — PO8/PO9 (Sprint 2026-08-05)
 *
 * Two-tab view backed by the compliance engine:
 *   1) By-requirement aggregate — how many bookings are pending, expired,
 *      waived, or met for each compliance requirement type on upcoming shifts.
 *   2) Expiring soon — a live list of pending shift-critical requirements that
 *      will hit their deadline within the selected horizon. Row-scoped
 *      actions: Waive (permanent), Extend (bump deadline), View booking.
 *
 * The dashboard is INFORMATIONAL — the compliance engine never hard-blocks
 * matching or booking. Missing requirements are treated as gaps (badges),
 * not walls, per the "Gaps as Badges" product rule.
 */
const REQ_ICON = {
  idv:              <ShieldCheck className="w-4 h-4 text-cyan-600" />,
  background_check: <Shield className="w-4 h-4 text-amber-600" />,
  ggf_certification:<Shield className="w-4 h-4 text-indigo-600" />,
  agreements:       <Shield className="w-4 h-4 text-slate-600" />,
  w9:               <Shield className="w-4 h-4 text-emerald-600" />,
  banking:          <Shield className="w-4 h-4 text-emerald-600" />,
};

const AdminComplianceDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [backupAnalytics, setBackupAnalytics] = useState(null);
  const [reminderStats, setReminderStats] = useState(null);
  const [horizonHours, setHorizonHours] = useState(24);
  const [onlyShiftCritical, setOnlyShiftCritical] = useState(true);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [waiveDlg, setWaiveDlg] = useState(null);   // { deadline_id, label, talent_name, action }
  const [waiveReason, setWaiveReason] = useState('');
  const [extendUntil, setExtendUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get('/compliance/admin/dashboard');
      setDashboard(res.data);
    } catch (e) {
      toast.error('Failed to load compliance dashboard');
    }
  }, []);

  const loadExpiring = useCallback(async () => {
    try {
      const res = await api.get('/compliance/admin/expiring', {
        params: { hours: horizonHours, only_shift_critical: onlyShiftCritical },
      });
      setExpiring(res.data.entries || []);
    } catch (e) {
      toast.error('Failed to load expiring compliance list');
    }
  }, [horizonHours, onlyShiftCritical]);

  const loadBackupAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/admin/backup-analytics', { params: { days: 30 } });
      setBackupAnalytics(res.data);
    } catch (e) {
      // Non-fatal — analytics is a widget, not the whole dashboard.
      setBackupAnalytics(null);
    }
  }, []);

  const loadReminderStats = useCallback(async () => {
    try {
      const res = await api.get('/compliance/admin/reminder-stats', { params: { days: 30 } });
      setReminderStats(res.data);
    } catch (e) {
      setReminderStats(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadExpiring(), loadBackupAnalytics(), loadReminderStats()]);
      setLoading(false);
    })();
  }, [loadDashboard, loadExpiring, loadBackupAnalytics, loadReminderStats]);

  const refresh = async () => {
    setReloading(true);
    await Promise.all([loadDashboard(), loadExpiring(), loadBackupAnalytics(), loadReminderStats()]);
    setReloading(false);
  };

  const openWaiveDialog = (entry, action) => {
    setWaiveDlg({
      deadline_id: entry.deadline_id,
      label:       entry.label,
      talent_name: entry.talent_name,
      action,
    });
    setWaiveReason('');
    setExtendUntil('');
  };

  const submitAction = async () => {
    if (!waiveDlg) return;
    if (!waiveReason || waiveReason.trim().length < 3) {
      toast.error('A reason (3+ chars) is required.');
      return;
    }
    if (waiveDlg.action === 'extend' && !extendUntil) {
      toast.error('Pick a new deadline.');
      return;
    }
    setSubmitting(true);
    try {
      if (waiveDlg.action === 'waive') {
        await api.post(`/compliance/waive/${waiveDlg.deadline_id}`, { reason: waiveReason.trim() });
        toast.success(`Waived ${waiveDlg.label} for ${waiveDlg.talent_name}`);
      } else {
        // extend
        await api.post(`/compliance/extend/${waiveDlg.deadline_id}`, {
          reason: waiveReason.trim(),
          new_deadline: new Date(extendUntil).toISOString(),
        });
        toast.success(`Extended ${waiveDlg.label} to ${new Date(extendUntil).toLocaleString()}`);
      }
      setWaiveDlg(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  const byReq = dashboard?.by_requirement || [];
  const totals = byReq.reduce(
    (acc, r) => ({
      pending: acc.pending + r.pending,
      expired: acc.expired + r.expired,
      waived:  acc.waived  + r.waived,
      met:     acc.met     + r.met,
    }),
    { pending: 0, expired: 0, waived: 0, met: 0 },
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-compliance-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}
              data-testid="back-to-admin-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-cyan-600" /> Compliance Dashboard
              </h1>
              <div className="text-xs text-slate-500">
                Track shift-critical + payout deadlines. Informational — never hard-blocks work.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={refresh} disabled={reloading}
            data-testid="refresh-compliance-btn">
            {reloading
              ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
              : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/talent-onboarding')}
            data-testid="goto-onboarding-compliance-btn"
            className="border-cyan-300 text-cyan-700 hover:bg-cyan-50">
            <ShieldCheck className="w-4 h-4 mr-1" /> Talent Onboarding Compliance
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/payment-holds')}
            data-testid="goto-payment-holds-btn"
            className="border-orange-300 text-orange-700 hover:bg-orange-50">
            <ShieldCheck className="w-4 h-4 mr-1" /> Payment Holds
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Aggregate totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Pending"       value={totals.pending} tone="cyan"
            icon={<Clock className="w-5 h-5" />} testId="stat-pending" />
          <StatCard label="Expired"       value={totals.expired} tone="red"
            icon={<XOctagon className="w-5 h-5" />} testId="stat-expired" />
          <StatCard label="Waived / Extended" value={totals.waived} tone="amber"
            icon={<AlertTriangle className="w-5 h-5" />} testId="stat-waived" />
          <StatCard label="Met"            value={totals.met}     tone="emerald"
            icon={<CheckCircle2 className="w-5 h-5" />} testId="stat-met" />
        </div>

        {/* By-requirement breakdown */}
        <Card data-testid="by-requirement-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-600" /> By Requirement (upcoming shifts)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {byReq.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">
                No compliance rows yet. They initialise automatically as bookings are created.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 px-2">Requirement</th>
                      <th className="text-left py-2 px-2">Type</th>
                      <th className="text-right py-2 px-2">Pending</th>
                      <th className="text-right py-2 px-2">Expired</th>
                      <th className="text-right py-2 px-2">Waived</th>
                      <th className="text-right py-2 px-2">Met</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byReq.map(r => (
                      <tr key={r.code} className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`req-row-${r.code}`}>
                        <td className="py-2 px-2 flex items-center gap-2 font-medium">
                          {REQ_ICON[r.code] || <Shield className="w-4 h-4 text-slate-400" />}
                          {r.label}
                        </td>
                        <td className="py-2 px-2">
                          {r.is_shift_critical && (
                            <Badge className="bg-red-50 text-red-700 border-red-200 border text-[10px] mr-1">
                              SHIFT-CRITICAL
                            </Badge>
                          )}
                          {r.is_payout_blocker && (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">
                              PAYOUT
                            </Badge>
                          )}
                          {!r.is_shift_critical && !r.is_payout_blocker && (
                            <span className="text-xs text-slate-400">Informational</span>
                          )}
                        </td>
                        <td className={`text-right py-2 px-2 font-semibold ${r.pending > 0 ? 'text-cyan-700' : 'text-slate-400'}`}
                          data-testid={`pending-${r.code}`}>
                          {r.pending}
                        </td>
                        <td className={`text-right py-2 px-2 font-semibold ${r.expired > 0 ? 'text-red-700' : 'text-slate-400'}`}
                          data-testid={`expired-${r.code}`}>
                          {r.expired}
                        </td>
                        <td className="text-right py-2 px-2 text-amber-700">{r.waived}</td>
                        <td className="text-right py-2 px-2 text-emerald-700">{r.met}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PO5 — Backup activation widget */}
        {backupAnalytics && backupAnalytics.total_backup_bookings > 0 && (
          <Card data-testid="backup-analytics-widget">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <ArrowUpCircle className="w-4 h-4 text-amber-600" />
                Backup Activation
                <span className="text-xs font-normal text-slate-500">
                  · last {backupAnalytics.days} days
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                  data-testid="ba-total">
                  <div className="text-[10px] font-bold text-amber-700 uppercase">Backup Bookings</div>
                  <div className="text-2xl font-black text-amber-900">
                    {backupAnalytics.total_backup_bookings}
                  </div>
                </div>
                <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3"
                  data-testid="ba-offered">
                  <div className="text-[10px] font-bold text-cyan-700 uppercase">Promotions Offered</div>
                  <div className="text-2xl font-black text-cyan-900">
                    {backupAnalytics.promotions_offered}
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"
                  data-testid="ba-accepted">
                  <div className="text-[10px] font-bold text-emerald-700 uppercase">Accepted</div>
                  <div className="text-2xl font-black text-emerald-900">
                    {backupAnalytics.promotions_accepted}
                  </div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3"
                  data-testid="ba-cancelled">
                  <div className="text-[10px] font-bold text-red-700 uppercase">Backup Cancels</div>
                  <div className="text-2xl font-black text-red-900">
                    {backupAnalytics.backups_cancelled}
                  </div>
                </div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3"
                  data-testid="ba-rate">
                  <div className="text-[10px] font-bold text-indigo-700 uppercase">Activation Rate</div>
                  <div className="text-2xl font-black text-indigo-900">
                    {Math.round((backupAnalytics.activation_rate || 0) * 100)}%
                  </div>
                </div>
              </div>
              {backupAnalytics.by_shift.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs font-semibold text-slate-600 cursor-pointer hover:text-cyan-700">
                    Top {backupAnalytics.by_shift.length} shifts with backup activity ↓
                  </summary>
                  <div className="mt-2 space-y-1">
                    {backupAnalytics.by_shift.map(s => (
                      <div key={s.shift_id}
                        className="flex items-center justify-between text-xs p-2 border border-slate-100 rounded hover:bg-slate-50"
                        data-testid={`ba-shift-${s.shift_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {s.event_title || s.venue_name}
                          </div>
                          <div className="text-slate-500">
                            {s.shift_date ? new Date(s.shift_date).toLocaleDateString() : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px]">
                            {s.backup_count} backup{s.backup_count === 1 ? '' : 's'}
                          </Badge>
                          {s.offered > 0 && (
                            <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200 border text-[10px]">
                              {s.offered} offered
                            </Badge>
                          )}
                          {s.cancelled > 0 && (
                            <Badge className="bg-red-50 text-red-700 border-red-200 border text-[10px]">
                              {s.cancelled} cancelled
                            </Badge>
                          )}
                          <button
                            onClick={() => navigate(`/admin/shifts/${s.shift_id}`)}
                            className="text-cyan-700 underline hover:text-cyan-900"
                            data-testid={`ba-shift-link-${s.shift_id}`}
                          >
                            View →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        )}

        {/* PO8/PO9 — Reminders Sent tile */}
        {reminderStats && (
          <Card data-testid="reminder-stats-widget">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Bell className="w-4 h-4 text-cyan-600" />
                Reminders Sent
                <span className="text-xs font-normal text-slate-500">
                  · last {reminderStats.days} days
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3" data-testid="rs-24h">
                  <div className="text-[10px] font-bold text-cyan-700 uppercase">24h Nudge</div>
                  <div className="text-2xl font-black text-cyan-900">{reminderStats.counts['24h']}</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="rs-6h">
                  <div className="text-[10px] font-bold text-amber-700 uppercase">6h Urgent</div>
                  <div className="text-2xl font-black text-amber-900">{reminderStats.counts['6h']}</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="rs-1h">
                  <div className="text-[10px] font-bold text-red-700 uppercase">1h Last Call</div>
                  <div className="text-2xl font-black text-red-900">{reminderStats.counts['1h']}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="rs-total">
                  <div className="text-[10px] font-bold text-slate-700 uppercase">Total</div>
                  <div className="text-2xl font-black text-slate-900">{reminderStats.counts.total}</div>
                </div>
              </div>
              {reminderStats.recent.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs font-semibold text-slate-600 cursor-pointer hover:text-cyan-700">
                    Recent {reminderStats.recent.length} reminders ↓
                  </summary>
                  <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                    {reminderStats.recent.map(e => {
                      const kindTone = {
                        '24h': 'bg-cyan-50 text-cyan-700 border-cyan-200',
                        '6h':  'bg-amber-50 text-amber-700 border-amber-200',
                        '1h':  'bg-red-50 text-red-700 border-red-200',
                      }[e.kind] || 'bg-slate-50 text-slate-700 border-slate-200';
                      return (
                        <div key={e.id} className="flex items-center justify-between text-xs p-2 border border-slate-100 rounded"
                          data-testid={`rs-event-${e.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">
                              {e.requirement} · {e.event_title || e.venue_name}
                            </div>
                            <div className="text-slate-500 truncate">→ {e.sent_to}</div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Badge className={`${kindTone} border text-[10px]`}>{e.kind}</Badge>
                            <span className="text-slate-400 text-[10px]">
                              {new Date(e.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        )}

        {/* Expiring soon */}
        <Card data-testid="expiring-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-red-600" /> Expiring Soon
              </CardTitle>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Horizon</Label>
                <select
                  className="border border-slate-300 rounded px-2 py-1 text-xs"
                  value={horizonHours}
                  onChange={(e) => setHorizonHours(parseInt(e.target.value, 10))}
                  data-testid="horizon-select"
                >
                  <option value={6}>6 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={48}>48 hours</option>
                  <option value={72}>72 hours</option>
                  <option value={168}>7 days</option>
                </select>
                <label className="text-xs flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={onlyShiftCritical}
                    onChange={(e) => setOnlyShiftCritical(e.target.checked)}
                    data-testid="critical-only-checkbox"
                  />
                  Shift-critical only
                </label>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {expiring.length === 0 ? (
              <div className="text-sm text-emerald-700 text-center py-8 bg-emerald-50 rounded-lg border border-emerald-200">
                <CheckCircle2 className="inline w-5 h-5 mr-1" />
                Nothing expiring in the next {horizonHours}h. Good work.
              </div>
            ) : (
              <div className="space-y-2">
                {expiring.map(e => (
                  <div key={e.deadline_id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg bg-white hover:bg-slate-50"
                    data-testid={`expiring-row-${e.deadline_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{e.talent_name}</span>
                        {e.is_shift_critical && (
                          <Badge className="bg-red-50 text-red-700 border-red-200 border text-[10px]">
                            SHIFT-CRITICAL
                          </Badge>
                        )}
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200 border text-[10px]">
                          {e.assignment_type}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {e.label} · {e.event_title || e.venue_name} · {new Date(e.shift_date).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-red-600 font-medium mt-0.5">
                        Deadline: {new Date(e.deadline_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline"
                        onClick={() => openWaiveDialog(e, 'extend')}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`extend-btn-${e.deadline_id}`}>
                        Extend
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => openWaiveDialog(e, 'waive')}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`waive-btn-${e.deadline_id}`}>
                        Waive
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => navigate(`/admin/shifts/${e.shift_id}`)}
                        className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                        data-testid={`view-shift-btn-${e.deadline_id}`}>
                        Shift
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Waive / Extend Dialog */}
      <Dialog open={!!waiveDlg} onOpenChange={(o) => !o && setWaiveDlg(null)}>
        <DialogContent data-testid="waive-extend-dialog">
          <DialogHeader>
            <DialogTitle>
              {waiveDlg?.action === 'waive' ? 'Waive Requirement' : 'Extend Deadline'}
            </DialogTitle>
            <DialogDescription>
              {waiveDlg?.label} for <b>{waiveDlg?.talent_name}</b>
              <br />
              <span className="text-xs text-slate-500">
                {waiveDlg?.action === 'waive'
                  ? 'Waiving permanently removes this requirement for this booking. This is audited.'
                  : 'Bump the deadline to a new time. The booking stays pending until met or the new deadline expires.'}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {waiveDlg?.action === 'extend' && (
              <div>
                <Label className="text-xs">New deadline</Label>
                <Input
                  type="datetime-local"
                  value={extendUntil}
                  onChange={(e) => setExtendUntil(e.target.value)}
                  data-testid="extend-datetime-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Reason <span className="text-red-600">*</span></Label>
              <Textarea
                value={waiveReason}
                onChange={(e) => setWaiveReason(e.target.value)}
                placeholder="Required — explain why this action is being taken (audited)"
                rows={3}
                data-testid="waive-reason-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiveDlg(null)}
              data-testid="cancel-waive-btn">
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={submitting}
              className={waiveDlg?.action === 'waive'
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'}
              data-testid="confirm-waive-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {waiveDlg?.action === 'waive' ? 'Waive' : 'Extend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, tone, icon, testId }) => {
  const tones = {
    cyan:    'bg-cyan-50 text-cyan-800 border-cyan-200',
    red:     'bg-red-50 text-red-800 border-red-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
        {icon} {label}
      </div>
      <div className="text-3xl font-black mt-1" data-testid={`${testId}-value`}>{value}</div>
    </div>
  );
};

export default AdminComplianceDashboard;
